/**
 * Pulls the audio out of a large MP4 without downloading the video.
 *
 * A station's audio-only rendition is sometimes cut short while its full
 * video file is fine -- but that file is gigabytes, nearly all of it
 * picture. An MP4 carries an index (the moov box) saying exactly where
 * every audio sample sits, so the index is read, and then only those byte
 * ranges are fetched: about 3% of the file. The AAC frames are re-wrapped
 * as an ADTS stream, which any decoder reads.
 *
 * Pure: everything goes through the `read` function, so the same code runs
 * in the browser (fetch with Range) and under test.
 */

/** Reads bytes [start, endInclusive] of the file. */
export type RangeReader = (start: number, endInclusive: number) => Promise<Uint8Array>;

export interface AudioPlan {
  /** AAC profile, sample-rate index and channel layout, for the ADTS headers. */
  objectType: number;
  freqIndex: number;
  channels: number;
  durationS: number;
  /** One per stored chunk: where it starts and the size of each frame in it. */
  chunks: { offset: number; sizes: number[] }[];
  /** Size of the ADTS stream this will produce. */
  outputBytes: number;
}

const u32 = (b: Uint8Array, o: number) => ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
const u64 = (b: Uint8Array, o: number) => u32(b, o) * 2 ** 32 + u32(b, o + 4);
const fourcc = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

interface Box { type: string; start: number; end: number } // payload bounds within the buffer

/** Child boxes of the payload [start, end). */
function boxes(b: Uint8Array, start: number, end: number): Box[] {
  const out: Box[] = [];
  let p = start;
  while (p + 8 <= end) {
    let size = u32(b, p);
    let header = 8;
    if (size === 1) { size = u64(b, p + 8); header = 16; }
    else if (size === 0) size = end - p;
    if (size < header || p + size > end) break;
    out.push({ type: fourcc(b, p + 4), start: p + header, end: p + size });
    p += size;
  }
  return out;
}
const child = (b: Uint8Array, parent: Box, type: string) => boxes(b, parent.start, parent.end).find((x) => x.type === type);

/** Finds the moov box by walking the top-level boxes, a header at a time. */
async function readMoov(read: RangeReader, fileSize: number): Promise<Uint8Array> {
  let pos = 0;
  while (pos + 8 <= fileSize) {
    const h = await read(pos, Math.min(pos + 15, fileSize - 1));
    let size = u32(h, 0);
    if (size === 1) size = u64(h, 8);
    else if (size === 0) size = fileSize - pos;
    if (size < 8) break;
    if (fourcc(h, 4) === "moov") {
      if (size > 64 * 1024 * 1024) throw new Error("This recording's index is too large to read in a browser.");
      return read(pos, pos + size - 1);
    }
    pos += size;
  }
  throw new Error("This video file has no index, so its audio can't be located.");
}

/** MPEG-4 descriptor: tag, then a length in 7-bit groups. Returns payload bounds. */
function descriptor(b: Uint8Array, p: number): { tag: number; start: number; end: number } {
  const tag = b[p++]!;
  let len = 0;
  for (let i = 0; i < 4; i++) {
    const c = b[p++]!;
    len = (len << 7) | (c & 0x7f);
    if (!(c & 0x80)) break;
  }
  return { tag, start: p, end: p + len };
}

/** The two-byte AudioSpecificConfig inside the track's esds box. */
function audioConfig(b: Uint8Array, stsd: Box): { objectType: number; freqIndex: number; channels: number } {
  // stsd: version/flags(4) + entry count(4), then the sample entry.
  const entry = boxes(b, stsd.start + 8, stsd.end)[0];
  if (!entry || entry.type !== "mp4a") throw new Error("The recording's audio isn't AAC, so it can't be extracted this way.");
  // The audio sample entry has 28 bytes of fixed fields before its child boxes.
  const esds = boxes(b, entry.start + 28, entry.end).find((x) => x.type === "esds");
  if (!esds) throw new Error("The recording's audio has no decoder configuration.");
  let p = esds.start + 4; // version/flags
  const es = descriptor(b, p);
  if (es.tag !== 0x03) throw new Error("Unexpected audio configuration.");
  p = es.start + 2; // ES_ID
  const flags = b[p++]!;
  if (flags & 0x80) p += 2;
  if (flags & 0x40) p += 1 + b[p]!;
  if (flags & 0x20) p += 2;
  const dec = descriptor(b, p);
  if (dec.tag !== 0x04) throw new Error("Unexpected audio configuration.");
  const dsi = descriptor(b, dec.start + 13);
  if (dsi.tag !== 0x05 || dsi.end - dsi.start < 2) throw new Error("Unexpected audio configuration.");
  const b0 = b[dsi.start]!, b1 = b[dsi.start + 1]!;
  const objectType = b0 >> 3;
  const freqIndex = ((b0 & 7) << 1) | (b1 >> 7);
  const channels = (b1 >> 3) & 0x0f;
  // ADTS can describe only the plain profiles, indexed rates and layouts.
  if (objectType < 1 || objectType > 4 || freqIndex > 12 || channels < 1 || channels > 7) {
    throw new Error("The recording's audio format can't be extracted this way.");
  }
  return { objectType, freqIndex, channels };
}

/** Reads the index and works out where every audio frame is. */
export async function planAudioExtraction(read: RangeReader, fileSize: number): Promise<AudioPlan> {
  const moov = await readMoov(read, fileSize);
  const top = boxes(moov, 0, moov.length)[0];
  if (!top || top.type !== "moov") throw new Error("The recording's index couldn't be read.");

  for (const trak of boxes(moov, top.start, top.end).filter((x) => x.type === "trak")) {
    const mdia = child(moov, trak, "mdia");
    const hdlr = mdia && child(moov, mdia, "hdlr");
    if (!mdia || !hdlr || fourcc(moov, hdlr.start + 8) !== "soun") continue;

    const mdhd = child(moov, mdia, "mdhd")!;
    const v1 = moov[mdhd.start] === 1;
    const timescale = u32(moov, mdhd.start + (v1 ? 20 : 12));
    const duration = v1 ? u64(moov, mdhd.start + 24) : u32(moov, mdhd.start + 16);

    const minf = child(moov, mdia, "minf");
    const stbl = minf && child(moov, minf, "stbl");
    const stsd = stbl && child(moov, stbl, "stsd");
    const stsz = stbl && child(moov, stbl, "stsz");
    const stsc = stbl && child(moov, stbl, "stsc");
    const stco = stbl && (child(moov, stbl, "stco") ?? child(moov, stbl, "co64"));
    if (!stbl || !stsd || !stsz || !stsc || !stco) throw new Error("The recording's audio index is incomplete.");

    const fixed = u32(moov, stsz.start + 4);
    const count = u32(moov, stsz.start + 8);
    const sizeOf = (i: number) => (fixed !== 0 ? fixed : u32(moov, stsz.start + 12 + i * 4));

    const wide = stco.type === "co64";
    const chunkCount = u32(moov, stco.start + 4);
    const offsetOf = (i: number) => (wide ? u64(moov, stco.start + 8 + i * 8) : u32(moov, stco.start + 8 + i * 4));

    // stsc runs: from chunk N (1-based) onward, each chunk holds K samples.
    const runs = Array.from({ length: u32(moov, stsc.start + 4) }, (_x, i) => ({
      first: u32(moov, stsc.start + 8 + i * 12), per: u32(moov, stsc.start + 12 + i * 12),
    }));

    const chunks: AudioPlan["chunks"] = [];
    let sample = 0;
    let outputBytes = 0;
    let run = 0;
    for (let c = 0; c < chunkCount && sample < count; c++) {
      while (run + 1 < runs.length && c + 1 >= runs[run + 1]!.first) run++;
      const per = Math.min(runs[run]!.per, count - sample);
      const sizes: number[] = [];
      for (let k = 0; k < per; k++) { const s = sizeOf(sample++); sizes.push(s); outputBytes += s + 7; }
      chunks.push({ offset: offsetOf(c), sizes });
    }
    if (chunks.length === 0) throw new Error("The recording's audio track is empty.");
    return { ...audioConfig(moov, stsd), durationS: timescale ? duration / timescale : 0, chunks, outputBytes };
  }
  throw new Error("This video file has no audio track.");
}

/** A 7-byte ADTS header for one AAC frame of `payload` bytes. */
function adtsHeader(plan: AudioPlan, payload: number): Uint8Array {
  const len = payload + 7;
  return Uint8Array.of(
    0xff, 0xf1,
    ((plan.objectType - 1) << 6) | (plan.freqIndex << 2) | (plan.channels >> 2),
    ((plan.channels & 3) << 6) | (len >> 11),
    (len >> 3) & 0xff,
    ((len & 7) << 5) | 0x1f,
    0xfc,
  );
}

/** Chunks this close together are fetched in one request. */
const COALESCE_GAP = 64 * 1024;
const MAX_GROUP_BYTES = 4 * 1024 * 1024;

/**
 * Yields the audio as ADTS, in order. Ranges are fetched `concurrency` at a
 * time; a failed range is retried, since one dropped request among
 * thousands should not cost the whole transfer.
 */
export async function* extractAdts(
  read: RangeReader, plan: AudioPlan, opts: { concurrency?: number; onProgress?: (bytesOut: number) => void } = {},
): AsyncGenerator<Uint8Array> {
  // Group neighbouring chunks into single range requests.
  const groups: { start: number; end: number; chunks: AudioPlan["chunks"] }[] = [];
  for (const c of plan.chunks) {
    const size = c.sizes.reduce((a, b) => a + b, 0);
    const g = groups[groups.length - 1];
    if (g && c.offset >= g.end && c.offset - g.end <= COALESCE_GAP && c.offset + size - g.start <= MAX_GROUP_BYTES) {
      g.end = c.offset + size;
      g.chunks.push(c);
    } else {
      groups.push({ start: c.offset, end: c.offset + size, chunks: [c] });
    }
  }

  const fetchGroup = async (g: (typeof groups)[number]): Promise<Uint8Array> => {
    let bytes: Uint8Array | null = null;
    for (let attempt = 0; attempt < 4 && !bytes; attempt++) {
      try {
        const got = await read(g.start, g.end - 1);
        if (got.length === g.end - g.start) bytes = got;
      } catch { /* retried below */ }
      if (!bytes) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
    if (!bytes) throw new Error("The video host stopped answering while the audio was being read.");
    const frames = g.chunks.reduce((n, c) => n + c.sizes.length, 0);
    const out = new Uint8Array(g.end - g.start - gapBytes(g) + frames * 7);
    let o = 0;
    for (const c of g.chunks) {
      let p = c.offset - g.start;
      for (const s of c.sizes) {
        out.set(adtsHeader(plan, s), o); o += 7;
        out.set(bytes.subarray(p, p + s), o); o += s; p += s;
      }
    }
    return out.subarray(0, o);
  };
  const gapBytes = (g: (typeof groups)[number]) =>
    g.end - g.start - g.chunks.reduce((n, c) => n + c.sizes.reduce((a, b) => a + b, 0), 0);

  const n = Math.max(1, opts.concurrency ?? 6);
  let done = 0;
  for (let i = 0; i < groups.length; i += n) {
    const batch = await Promise.all(groups.slice(i, i + n).map(fetchGroup));
    for (const part of batch) {
      done += part.length;
      opts.onProgress?.(done);
      yield part;
    }
  }
}
